<template>
  <div class="shadow-card max-h-full overflow-auto rounded-lg bg-white">
    <table class="min-w-full">
      <thead class="sticky top-0 z-20">
        <tr class="bg-zinc-50 after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:bg-zinc-200">
          <th class="whitespace-nowrap px-8 py-4 text-left text-sm font-medium uppercase leading-normal text-zinc-800">{{ __('Job Title') }}</th>
          <th v-if="actions.show?.recruiter" class="whitespace-nowrap px-8 py-4 text-center text-sm font-medium uppercase leading-normal text-zinc-800">{{ __('Recruiter') }}</th>
          <th class="whitespace-nowrap px-8 py-4 text-center text-sm font-medium uppercase leading-normal text-zinc-800">{{ __('Contract type') }}</th>
          <th class="whitespace-nowrap px-8 py-4 text-center text-sm font-medium uppercase leading-normal text-zinc-600">{{ __('Status') }}</th>
          <th class="sticky right-0 whitespace-nowrap bg-inherit px-6 py-4 text-center text-sm font-medium uppercase leading-normal text-zinc-800">{{ __('Action') }}</th>
        </tr>
      </thead>

      <tbody class="divide-y divide-zinc-200">
        <tr v-if="isLoading">
          <td colspan="9" class="whitespace-nowrap px-6 py-4 text-zinc-600">{{ __('Loading...') }}</td>
        </tr>
        <tr v-else-if="!requisitions.length">
          <td colspan="9" class="whitespace-nowrap px-6 py-4 text-zinc-600">{{ __('There are no job requisitions yet.') }}</td>
        </tr>

        <template v-else>
          <tr v-for="(requisition, index) in requisitions" :key="index" class="bg-white hover:bg-zinc-50">
            <td class="whitespace-nowrap px-6 py-4 text-left text-zinc-600">{{ requisition.role }}</td>
            <td v-if="requisition.show.recruiter" class="whitespace-nowrap px-6 py-4 text-center text-zinc-600">{{ requisition.recruiter }} <span class="text-sm color-warning">{{ requisition.recruiter_state }}</span></td>
            <td class="whitespace-nowrap px-6 py-4 text-center text-zinc-600">{{ requisition.contract_type }}</td>
            <td class="whitespace-nowrap px-6 py-4 text-center text-zinc-800">
              <span :class="`${getStatusClasses(requisition.status.id)} rounded-full px-3 py-1 text-sm`">{{ requisition.status.name }}</span>
            </td>
            <template v-if="requisition.can.create || requisition.can.delete || requisition.can.duplicate || requisition.can.approve">
            <td class="sticky right-0 whitespace-nowrap bg-inherit pr-6 pl-4 py-4 text-left text-zinc-600 flex items-center justify-center gap-1">
              <div v-if="requisition.can.create" class="w-[2.25rem] flex items-center justify-start px-2">
                <a :href="requisition.edit_requisition" class="w-full">
                  <Icon name="edit" class="!text-2xl text-zinc-600 !block text-center" />
                </a>
              </div>
              <div v-if="requisition.can.delete" class="w-[2.25rem] flex items-center justify-start px-2">
                <span @click="handleDeleteRequisitionButtonClick(requisition)" class="w-full cursor-pointer">
                  <Icon name="delete" class="!text-2xl text-zinc-600 !block text-center" />
                </span>
              </div>
              <div v-if="requisition.can.duplicate" class="w-[2.25rem] flex items-center justify-start px-2">
                <span @click="handleDuplicateRequisitionButtonClick(requisition)" class="w-full cursor-pointer">
                  <Icon name="content_copy" class="!text-2xl text-zinc-600 !block text-center" />
                </span>
              </div>
              <div v-if="requisition.can.approve" class="ml-4">
                <a :href="requisition.create_job_from_requisition">
                  <Button>{{ __('Add job') }}</Button>
                </a>
              </div>
            </td>
            </template>
            <template v-else>
              <td class="pl-[0.8rem]">
                <p>{{ __('No actions') }}</p>
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>

    <DeleteRequisitionModal
        v-if="isDeleteRequisitionModalOpen"
        :requisition="selectedRequisition"
        @update="handleDeleteRequisitionModalUpdate"
        @close="handleDeleteRequisitionModalClose"
    />
    <DuplicateRequisitionModal
        v-if="isDuplicateRequisitionModalOpen"
        :requisition="selectedRequisition"
        @update="handleDuplicateRequisitionModalUpdate"
        @close="handleDuplicateRequisitionModalClose"
    />

  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { Button } from '@/components/block/button';
  import { ApproveRequisitionModal } from './components/approve-requisition-modal';
  import {Icon} from "../../../../../components/global";
  import {DeleteRequisitionModal} from "./components/delete-requisition-modal";
  import {DuplicateRequisitionModal} from "./components/duplicate-requisition-modal";

  const props = defineProps({
    requisitions: {
      type: Array,
      default: () => []
    },
    actions: {
      type: Object,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  });

  const emits = defineEmits(['update']);

  const selectedRequisition = ref(null);
  const isDeleteRequisitionModalOpen = ref(false);
  const isDuplicateRequisitionModalOpen = ref(false);

  function getStatusClasses(status) {
    switch (status) {
      case 0:
        return 'bg-yellow-500/25';
      case 1:
        return 'bg-success/25';
      default:
        return 'bg-error/25';
    }
  }

  function handleDeleteRequisitionButtonClick(requisition) {
    selectedRequisition.value = requisition;
    isDeleteRequisitionModalOpen.value = true;
  }

  function handleDeleteRequisitionModalClose() {
    isDeleteRequisitionModalOpen.value = false;
  }

  function handleDuplicateRequisitionButtonClick(requisition) {
    selectedRequisition.value = requisition;
    isDuplicateRequisitionModalOpen.value = true;
  }
  function handleDuplicateRequisitionModalClose() {
    isDuplicateRequisitionModalOpen.value = false;
  }

  function handleDeleteRequisitionModalUpdate() {
    emits('update');
  }

  function handleDuplicateRequisitionModalUpdate() {
    emits('update');
  }

</script>
